import type { HandoffMode, IsoDateTime, MatchSource, Tv2mLinkContext } from '@apm/contracts';
import { TriggerError } from './tv2m.js';
import type { TranslateKey } from '@apm/i18n';
import type { MessageKey } from './i18n/ko.js';

/**
 * 핸드오프 응답 중 **화면이 읽는 부분**.
 *
 * 두 경로가 이 모양을 함께 만족한다 — federated 의 `BloomHandoffResponse`(BLOOM Server)와
 * delegated 의 `TriggerResponse`(TV2M). **모드는 배포 설정이지 서로 다른 코드가 아니므로**
 * (절대 규칙 17) 화면이 어느 쪽에서 왔는지 알 필요가 없고, 알면 판정이 두 벌이 된다.
 *
 * 여기에 `deepLinkUrl` 이 없는 것이 B9 의 핵심이다. federated Tier 0 은 그 값을 **아예
 * 소비하지 않는다** (`docs/07` §5 의 표 2번) — 시트가 상품을 직접 알기 때문이다.
 */
export interface HandoffOutcome {
  matchId: string;
  handoff: HandoffMode;
  matchedCount?: number;
  /**
   * 무엇으로 찾았는가 (`docs/21` W6). **없을 수 있다** — 위탁형의 `TriggerResponse` 에는
   * 이 필드가 없고, 매칭이 실패해도 비어 온다. 화면은 그때 기존 문구로 떨어진다.
   */
  matchSource?: MatchSource;
  qrUrl?: string;
  qrValidUntil?: IsoDateTime;
}

/**
 * 화면이 그리는 핸드오프 상태.
 *
 * ── `same_device` 가 상태가 됐다 (B9) ─────────────────────────
 *
 * v7 에서 이 자리에는 `same_device` 가 **없었다.** 그 경로는 상태가 아니라 이동이었기
 * 때문이다 — 응답을 받은 자리에서 `location.href` 를 바꾸고 이 화면은 사라졌다.
 *
 * **B9 이 그 한 줄을 지웠다** (절대 규칙 14: "어떤 tier 에서도 재생을 멈추지 않는다").
 * 두 앱이 한 오리진으로 합쳐진 뒤로는 옮길 이유가 없다 — 같은 앱 안의 상품 시트를 열고
 * 영상은 축소된 채 계속 간다. 그래서 `sheet` 가 여기 있고, 이제 **모든 tier 가 상태다.**
 *
 * `matchId` 는 낙관적으로 연 시점에는 없다 — 시트를 먼저 열고 서버 응답이 그것을 채운다.
 * `note` 는 그 응답이 실패했거나 예상과 달랐을 때의 한 줄이고, **시트를 닫지 않는다**
 * (`docs/14` B9 의 5번).
 */
/**
 * 시트 위의 한 줄. **문구가 아니라 키와 치환값이다** (절대 규칙 24) — 이 모듈은 순수
 * 함수라 훅을 부를 수 없다.
 */
export interface SheetNote {
  /**
   * **복수형 밑둥도 온다** (`TranslateKey`). `t('a.b', { n })` 는 사전에 `a.b_one` ·
   * `a.b_other` 가 있을 때 그 밑둥 `a.b` 를 받으므로, `MessageKey` 로만 좁히면
   * **복수형이 필요한 문구를 이 자리에 실을 수 없다.**
   */
  key: TranslateKey<MessageKey>;
  count?: number;
}

/**
 * ★ **시트가 여는 것이 광고 소재일 때의 신원** (v21/F13).
 *
 * ── 이것이 없어서 무슨 일이 났는가 ──────────────────────────
 *
 * `sheet` 상태에는 **무엇을 여는지가 없었다.** 화면이 그것을 「지금 재생 중인 회차」로
 * 채웠고, v16 까지는 그것이 언제나 참이었다 — 시청 화면의 트리거가 `channel_content`
 * 하나였기 때문이다. **v21 이 그 전제를 깼다**(절대 규칙 45 의 개정: 광고 중의 구매).
 *
 * 그래서 폰에서 광고를 보다 구매를 누르면 Tier 0 시트가 열리는데 그 시트가 여는 것은
 * **드라마 회차**였고, 일반 채널 회차의 `deepLink` 는 **빈 문자열이다**(그것이 규칙이다).
 * 빈 값은 `buildTrackingUrl` 에서 `?src=fast&…` 라는 **상대 URL** 이 되고, 설치된 앱에서
 * 상대 URL 은 **자기 오리진**이라 브라우저가 아니라 **앱 자신이** 그리로 간다 —
 * 증상은 「판매처로 이동을 누르면 시작 게이트가 뜬다」 하나였다.
 *
 * **홈쇼핑에서는 영원히 드러나지 않는다** — 그쪽 `deepLink` 는 실제 판매처 주소다.
 *
 * `productId` 와 `campaignId` 는 **둘 다 `Creative.id` 그대로다.** 조립하지 않는다 —
 * 폰이 그 값으로 `GET /v1/creatives/{campaignId}` 를 부른다 (`docs/45` §14 의 20).
 */
export interface AdSheetTarget {
  productId: string;
  campaignId: string;
}

/**
 * ★ **시트가 여는 것이 제휴 상품일 때의 신원** (v23, `docs/47` §9.3).
 *
 * **`AdSheetTarget` 과 모양이 같은데 타입을 나눈다.** 같은 이유로 `ad` 와 `offer` 를
 * 다른 필드로 둔 것이고(D9), **한 필드에 둘을 담으면 아래 `sheetLinkContext` 가
 * `triggerType` 을 정할 근거를 잃는다.**
 *
 * `productId` 와 `campaignId` 는 **둘 다 브로커의 `offerId` 그대로다.** 조립하지 않는다 —
 * 폰이 그 값으로 `GET /v1/creatives/{campaignId}` 를 부른다 (절대 규칙 62).
 */
export interface OfferSheetTarget {
  productId: string;
  campaignId: string;
}

export type HandoffState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  /**
   * `ad` 가 있으면 이 시트는 **광고 소재**를, `offer` 가 있으면 **제휴 상품**을 연다 —
   * 읽는 카탈로그도 나갈 곳도 그쪽이다. 둘 다 없으면 재생 중인 회차이고, 그것이
   * v16 까지의 유일한 경우였다.
   *
   * **셋이 동시에 참일 수 없다** — 세 진입점이 각각 하나만 씌운다.
   */
  | {
      kind: 'sheet';
      matchId?: string;
      note?: SheetNote;
      ad?: AdSheetTarget;
      offer?: OfferSheetTarget;
    }
  /**
   * `source` 는 문구의 근거이고 `qrUrl` 은 두 번째 선택지다 (`docs/21` W7·W8).
   *
   * **둘 다 없을 수 있고, 없다고 화면이 깨지지 않아야 한다** — 위탁형 응답에는
   * `matchSource` 가 없고, 계정 경로에는 QR 이 없다.
   */
  | { kind: 'push'; count: number; source?: MatchSource; qrUrl?: string; until?: string }
  | { kind: 'qr'; url: string; until?: string }
  | { kind: 'error'; reason: MessageKey };

/**
 * 「알림을 보냈습니다」 안내가 화면에 남는 시간 (v21/F13).
 *
 * **30초인 이유는 그것이 정보이기 때문이다.** 시청자가 폰을 집어 드는 데 드는 시간이고,
 * 그보다 길게 두면 **광고가 끝나고 콘텐츠로 돌아온 뒤에도 남아** 화면과 어긋난다 —
 * 상품이 바뀔 때 오버레이를 닫는 규칙(`docs/06` 「오버레이 규칙」)이 지키려던 그것이,
 * 광고 중에는 `itemKey` 가 안 바뀌어서 성립하지 않는다.
 */
export const HANDOFF_PUSH_LINGER_MS = 30_000;

/**
 * 이 오버레이를 **스스로 닫아도 되는가**, 된다면 몇 ms 뒤인가. `null` 이면 닫지 않는다.
 *
 * **가르는 것은 「시청자가 아직 할 일이 남았는가」다.**
 *
 * - `push` 인데 **QR 이 없다** — 계정으로 찾은 경우다. 폰이 이미 울렸고 화면에 남은 것은
 *   **안내뿐**이라 시간이 지나면 치운다
 * - `push` 인데 **QR 이 있다** — 가구 경로다(규칙 23). 알림이 안 닿았을 수 있어 **스캔이
 *   두 번째 선택지로 살아 있어야 한다.** QR 이 유효한 동안(`until`)까지 두고, 그 값이
 *   없으면 최소 30초는 준다
 * - `sheet` — **체크아웃 중이다.** 자동으로 닫으면 사던 것이 사라진다
 * - `qr` · `error` — 화면에 남은 것이 **시청자가 할 수 있는 일 전부**다
 *
 * **순수 함수로 두는 이유**는 이 판정이 `setTimeout` 안에 숨으면 테스트가 못 보기
 * 때문이다 (절대 규칙 31 의 「판정은 순수 함수가 반환값으로 낸다」와 같은 방침).
 */
export function handoffLingerMs(state: HandoffState, nowMs: number): number | null {
  if (state.kind !== 'push') return null;
  if (state.qrUrl === undefined) return HANDOFF_PUSH_LINGER_MS;
  if (state.until === undefined) return HANDOFF_PUSH_LINGER_MS;
  const until = Date.parse(state.until);
  if (!Number.isFinite(until)) return HANDOFF_PUSH_LINGER_MS;
  // **제안이 살아 있는 동안 남는다.** 이미 지난 값이면 최소 시간만 준다.
  return Math.max(HANDOFF_PUSH_LINGER_MS, until - nowMs);
}

export const HANDOFF_IDLE: HandoffState = { kind: 'idle' };

/** 낙관적으로 연 시트. 아직 서버가 답하지 않았다. */
export const HANDOFF_SHEET: HandoffState = { kind: 'sheet' };

/**
 * 응답을 화면 상태로 옮긴다. **순수 함수라 node 환경에서 검증된다** —
 * `client-type.ts` 와 같은 이유이고, 여기서 갈리는 것이 시청자가 보는 화면 전부다.
 *
 * **시트가 이미 열려 있으면 이 함수를 쓰지 않는다.** 그때는 아래 `sheetFromOutcome` 이다 —
 * 열린 시트를 응답이 닫아 버리면 시청자가 읽던 상품이 사라진다.
 */
export function handoffFromResponse(response: HandoffOutcome): HandoffState {
  switch (response.handoff) {
    case 'push':
      // `matchedCount` 가 없거나 0 이면 발송된 것이 없다는 뜻이다. "0대로 보냈습니다" 는
      // 안내가 아니라 혼란이라 오류 쪽으로 보낸다 — 시청자에게 필요한 것은 폴백 QR 이다.
      return response.matchedCount && response.matchedCount > 0
        ? {
            kind: 'push',
            count: response.matchedCount,
            ...(response.matchSource ? { source: response.matchSource } : {}),
            // 가구 경로는 QR 을 함께 낸다 (`docs/21` §2.4). 계정 경로에는 없고,
            // 그때는 아래 화면이 QR 자리를 그리지 않는다.
            ...(response.qrUrl ? { qrUrl: response.qrUrl } : {}),
            ...(response.qrValidUntil ? { until: response.qrValidUntil } : {}),
          }
        : { kind: 'error', reason: 'handoff.reason.noPhone' };

    case 'qr':
      // `qrUrl` 이 없는 `qr` 응답은 서버 결함이다. 빈 QR 을 그리면 스캔한 사람이 아무
      // 데도 가지 못하므로, 폴백이 있는 오류로 떨어뜨리는 편이 낫다.
      return response.qrUrl
        ? {
            kind: 'qr',
            url: response.qrUrl,
            ...(response.qrValidUntil ? { until: response.qrValidUntil } : {}),
          }
        : { kind: 'error', reason: 'handoff.reason.noQr' };

    case 'none':
      return { kind: 'error', reason: 'handoff.reason.notAvailable' };

    case 'same_device':
      /*
       * **같은 기기다. 시트를 연다** (절대 규칙 14).
       *
       * v7 은 여기서 오류를 냈다 — 그 경로는 호출부가 이미 `location.href` 로 떠났어야
       * 했고, 여기 닿았다는 것은 `deepLinkUrl` 이 비어 있었다는 뜻이었기 때문이다.
       * 이제 그 값을 아예 보지 않으므로 오류가 아니다.
       */
      return sheetFromOutcome(response);
  }
}

/**
 * 시트가 열린 채로 응답을 받았을 때. **시트를 닫지 않는다** (`docs/14` B9 의 5번).
 *
 * 낙관적으로 먼저 열었으므로 응답이 하는 일은 둘뿐이다 — `matchId` 를 채우는 것과,
 * 예상과 달랐으면 그 사실을 한 줄로 알리는 것. **닫으면 시청자가 읽던 상품이 사라진다.**
 *
 * 서버가 `same_device` 가 아닌 것을 골랐다면 대개 `TV2M_FORCE_TIER` 다(정상 경로에서
 * Tier 0 의 조건은 이 화면과 같은 `clientType === 'mobile_web'` 하나다). 그래도 이 기기에서
 * 살 수 있다는 사실은 변하지 않으므로 시트가 이긴다 — 바뀌는 것은 배지뿐이다.
 */
export function sheetFromOutcome(outcome: HandoffOutcome): HandoffState {
  return {
    kind: 'sheet',
    // 서버가 매칭에 실패하면 `matchId` 가 빈 문자열로 온다(`handoff.service.ts` 의
    // `{ matchId: '', handoff: 'none', recorded: false }`). 빈 값을 채우면 시트가
    // 있지도 않은 상관관계 키를 보여 준다.
    ...(outcome.matchId ? { matchId: outcome.matchId } : {}),
    ...(outcome.handoff === 'same_device' ? {} : { note: noteFromOutcome(outcome) }),
  };
}

/**
 * ★ **이 시트가 광고를 연다고 표시한다** (v21/F13).
 *
 * **응답을 해석한 뒤에 씌운다.** `handoffFromResponse` 는 tier 에 따라 `sheet`·`push`·
 * `qr`·`error` 중 하나를 주는데, **광고 여부는 그 판정과 무관하다** — 같은 클릭이
 * 폰에서는 시트가 되고 노트북에서는 알림이 된다. 그래서 판정은 한 벌로 두고
 * (절대 규칙 17 의 방침) 시트인 경우에만 신원을 붙인다.
 *
 * **누른 순간의 광고를 담는 것이 계약이다.** 서버 왕복 동안 광고가 넘어갔을 수 있고,
 * 그때 시청자가 보려는 것은 **누를 때 보던 그 광고**다.
 */
export function sheetForAd(state: HandoffState, target: AdSheetTarget): HandoffState {
  return state.kind === 'sheet' ? { ...state, ad: target } : state;
}

/**
 * ★ **이 시트가 제휴 상품을 연다고 표시한다** (v23). `sheetForAd` 와 같은 자리·같은 규율.
 *
 * **누른 순간의 상품을 담는 것이 계약이다.** 서버 왕복 동안 **카드가 걷혔을 수 있고**
 * (`displaySec` 이 15초다), 그때 시청자가 보려는 것은 **누를 때 보던 그 상품**이다.
 *
 * 담지 않으면 같은 기기(폰) 응답에서 시트가 **「재생 중인 회차」**로 채워지고, 일반 채널
 * 회차의 `deepLink` 는 `''` 라 **빈 값이 상대 URL 이 되어 앱이 자기 시작 게이트로 간다** —
 * v21/F13 이 광고에서 실기로 밟은 **바로 그 증상**이다.
 */
export function sheetForOffer(state: HandoffState, target: OfferSheetTarget): HandoffState {
  return state.kind === 'sheet' ? { ...state, offer: target } : state;
}

/**
 * ★ **시트가 여는 상품의 맥락** (v21/F13). **순수 함수다** — 이 갈림이 조용히 틀렸던
 * 자리라(위 `AdSheetTarget`) 화면 없이 무는 검사가 있어야 한다.
 *
 * 갈리는 것은 셋이고 **함께 갈린다** — 신원 · 읽어 올 카탈로그(`triggerType`) · 나갈 곳.
 * 하나만 바꾸면 「광고 제목인데 회차 판매처」 같은 섞인 화면이 되고, 그 화면은 그럴듯하다.
 */
export function sheetLinkContext(
  sheet: {
    matchId?: string | undefined;
    ad?: AdSheetTarget | undefined;
    offer?: OfferSheetTarget | undefined;
  },
  content: { productId: string; campaignId: string },
): Tv2mLinkContext {
  /*
   * ★ v23. **셋으로 갈린다. 그리고 컴파일이 이 자리를 못 잡는다.**
   *
   * 이 함수는 삼항이지 `Record<TriggerType, …>` 이 아니라, `TRIGGER_TYPES` 를 넓혔을 때
   * **세 곳을 짚어 준** 그 그물 밖에 있다 (`docs/47` §9.3 의 ★ · §14 의 22).
   * **`sheet.offer` 를 더해도 컴파일이 깨지지 않았다** — 사람이 찾아야 하는 자리다.
   *
   * 그대로 뒀다면 상품 시트가 **재생 중인 회차**로 채워지고 `triggerType` 이
   * `'channel_content'` 가 된다. 일반 채널 회차의 `deepLink` 는 `''` 라 **빈 값이 상대
   * URL 이 되어 앱이 자기 시작 게이트로 간다** — v21/F13 이 광고에서 실기로 밟은 그 증상이다.
   *
   * **갈리는 것은 셋이고 함께 갈린다** — 신원 · 읽어 올 카탈로그(`triggerType`) · 나갈 곳.
   * 하나만 바꾸면 「상품 제목인데 회차 판매처」 같은 섞인 화면이 되고, **그 화면은
   * 그럴듯하다.**
   */
  const target = sheet.offer ?? sheet.ad ?? content;
  const triggerType: Tv2mLinkContext['triggerType'] =
    sheet.offer !== undefined ? 'content_offer' : sheet.ad !== undefined ? 'ad' : 'channel_content';

  return {
    productId: target.productId,
    campaignId: target.campaignId,
    // 같은 기기에서 이어보는 중이라는 뜻이다 (`TriggerBadge` 의 문구 표).
    source: 'tv2m_same_device',
    triggerType,
    ...(sheet.matchId === undefined ? {} : { matchId: sheet.matchId }),
  };
}

/** 열린 시트에 한 줄을 붙인다. `matchId` 는 이미 받았으면 그대로 둔다. */
export function sheetWithNote(previous: HandoffState, note: SheetNote): HandoffState {
  return previous.kind === 'sheet' ? { ...previous, note } : previous;
}

/**
 * 무엇으로 찾았는지를 **시청자에게 하는 말**로 옮긴다 (`docs/21` W7).
 *
 * `account_matched` 같은 내부 이름을 그대로 노출하지 않는다 — 진단용 이름은 `?debug=1`
 * 패널의 몫이다.
 *
 * `null` 이면 화면이 그 줄을 그리지 않는다. **없는 경우가 정상 경로에 둘 있다** —
 * 위탁형 응답에는 `matchSource` 가 없고, `same_device` 는 같은 기기라 할 말이 없다.
 * 기본값으로 아무 문구나 채우면 「계정으로 찾았습니다」가 계정 없는 시청자에게 뜬다.
 */
export function matchSourceKey(source: MatchSource | undefined): MessageKey | null {
  switch (source) {
    case 'account_matched':
      return 'handoff.matchedByAccount';
    case 'server_ip_matched':
      return 'handoff.matchedByNetwork';
    default:
      return null;
  }
}

/**
 * 실패를 화면 문구로 옮긴다. **종류마다 시청자가 할 일이 다르다** — 그래서 `tv2m.ts` 가
 * 실패를 `TriggerError` 로 이름 붙여 올린다.
 */
export function handoffFromError(error: unknown): HandoffState {
  return { kind: 'error', reason: reasonKeyFromError(error) };
}

/**
 * 위와 같은 문구를 시트의 배지 자리에서도 쓴다. 두 곳이 다르면 같은 실패가 다르게 보인다.
 *
 * **문구가 아니라 사전 키다** (v9, 절대 규칙 24). 이 모듈은 순수 함수라 훅을 부를 수
 * 없고, 무엇보다 판정과 번역은 다른 일이다 — 화면이 `t()` 로 옮긴다.
 */
export function reasonKeyFromError(error: unknown): MessageKey {
  if (error instanceof TriggerError) {
    switch (error.kind) {
      case 'timeout':
        return 'handoff.reason.timeout';
      case 'network':
        return 'handoff.reason.network';
      case 'status':
        return 'handoff.reason.status';
    }
  }
  return 'handoff.reason.unknown';
}

/**
 * 시트가 열려 있는데 서버가 다른 tier 를 골랐을 때의 한 줄.
 *
 * **숨기지 않는다.** 이 기기에서 사면 되므로 시청자가 할 일은 없지만, 알림이 다른 폰에
 * 갔다면 그 사실을 모르는 편이 더 이상하다.
 */
function noteFromOutcome(outcome: HandoffOutcome): SheetNote {
  switch (outcome.handoff) {
    case 'push':
      return { key: 'handoff.note.pushedOthers', count: outcome.matchedCount ?? 0 };
    case 'qr':
      return { key: 'handoff.note.serverChoseQr' };
    case 'none':
    case 'same_device':
      return { key: 'handoff.note.notRecorded' };
  }
}
